import { daysUntil } from '@/lib/papertrade/expiry'
import { AccountAuthMethod, AccountStatus, PublicAccount } from '@/types/generated/common/account'
import { TradeVenue } from '@/types/generated/common/types'

/**
 * Whether an account is onboarded via a credential the generic manual
 * API-key/secret edit form can't correctly represent — no key/secret pair
 * to edit inline (wallet-delegated), or a credential shape the generic form
 * doesn't cover (Lighter: no `apiKey` field, needs `l1Address`/
 * `accountIndex`/`apiKeyIndex` alongside the secret).
 *
 * The backend only flips `authMethod` once onboarding completes, so an
 * account still mid-onboarding (status CREATING) reports UNSPECIFIED.
 * Hyperliquid (agent wallet) and Derive (session key) key off `authMethod`;
 * Lighter reports `ACCOUNT_AUTH_METHOD_API_KEY` like Bybit/Deribit/Upbit, so
 * it's keyed off `tradeVenue` instead, same as the CREATING-status fallback
 * the other two already rely on.
 */
export function isAgentWalletAccount(
  account: Pick<PublicAccount, 'authMethod' | 'tradeVenue'>
): boolean {
  return (
    account.authMethod === AccountAuthMethod.ACCOUNT_AUTH_METHOD_AGENT_WALLET ||
    account.authMethod === AccountAuthMethod.ACCOUNT_AUTH_METHOD_SESSION_KEY ||
    account.tradeVenue === TradeVenue.TRADE_VENUE_HYPERLIQUID ||
    account.tradeVenue === TradeVenue.TRADE_VENUE_DERIVE ||
    account.tradeVenue === TradeVenue.TRADE_VENUE_LIGHTER ||
    account.tradeVenue === TradeVenue.TRADE_VENUE_PAPERTRADE
  )
}

/**
 * Find a half-onboarded agent-wallet account to resume instead of minting a
 * duplicate. Agent-wallet venues (Hyperliquid, Derive) are one-per-user and
 * onboard over several signature attempts, so re-entering the create flow
 * for that venue should continue an existing CREATING account rather than
 * create a new one. API-key venues are intentionally multi-account, so they
 * never match here.
 */
export function findResumableAgentWalletAccount<
  T extends Pick<PublicAccount, 'authMethod' | 'tradeVenue' | 'status'>,
>(accounts: T[], tradeVenue: TradeVenue): T | undefined {
  return accounts.find(
    (account) =>
      account.tradeVenue === tradeVenue &&
      account.status === AccountStatus.ACCOUNT_STATUS_CREATING &&
      isAgentWalletAccount(account)
  )
}

export type StatusType =
  | 'active'
  | 'inactive'
  | 'suspended'
  | 'creating'
  | 'auth_failed'
  | 'pending_auth'
  | 'frozen'

export function toAccountStatus(account: PublicAccount): {
  title: string
  message: string
  isActive: boolean
  statusType: StatusType
} {
  switch (account.status) {
    case AccountStatus.ACCOUNT_STATUS_ACTIVE:
      return {
        title: 'Active',
        message: 'Account is active now',
        isActive: true,
        statusType: 'active',
      }
    case AccountStatus.ACCOUNT_STATUS_INACTIVE:
      return {
        title: 'Inactive',
        message: 'Account is inactive.\nPlease submit the api key and secret',
        isActive: false,
        statusType: 'inactive',
      }
    case AccountStatus.ACCOUNT_STATUS_SUSPENDED:
      return {
        title: 'Suspended',
        message: 'Account is suspended.',
        isActive: false,
        statusType: 'suspended',
      }
    case AccountStatus.ACCOUNT_STATUS_PENDING_AUTH:
      return {
        title: 'Pending Auth',
        message: 'Account is pending for authentication',
        isActive: false,
        statusType: 'pending_auth',
      }
    case AccountStatus.ACCOUNT_STATUS_AUTH_FAILED:
      return {
        title: 'Auth Failed',
        message: 'Account authentication failed. Please re-submit the API Key and Secret',
        isActive: false,
        statusType: 'auth_failed',
      }
    case AccountStatus.ACCOUNT_STATUS_CREATING:
      return {
        title: 'Creating',
        message: 'Account is creating.',
        isActive: false,
        statusType: 'creating',
      }
    case AccountStatus.ACCOUNT_STATUS_FROZEN:
      return {
        title: 'Frozen',
        message: 'Trading is frozen — your plan no longer covers this venue.',
        isActive: false,
        statusType: 'frozen',
      }
    default:
      return {
        title: 'Unknown',
        message: 'Unknown',
        isActive: false,
        statusType: 'inactive',
      }
  }
}

/** Banner lead time: show "Refresh connection" once the HL agent is within 30
 * days of expiry. Matches the backend banner threshold (locked decision). */
export const AGENT_EXPIRY_BANNER_THRESHOLD_MS = 30 * 24 * 60 * 60 * 1000

const DAY_MS = 24 * 60 * 60 * 1000

/** Banner lead time per venue. Papertrade's session key is short-lived, so it warns 3 days out
 * (the backend's `RESIGN_NOTICE`); everything else keeps the 30-day agent threshold. */
export function agentExpiryThresholdMs(tv: TradeVenue): number {
  return tv === TradeVenue.TRADE_VENUE_PAPERTRADE ? 3 * DAY_MS : AGENT_EXPIRY_BANNER_THRESHOLD_MS
}

export type HyperliquidReconnectReason = 'auth_failed' | 'expiring_soon'

export interface HyperliquidReconnectState {
  needsReconnect: boolean
  reason: HyperliquidReconnectReason | null
  daysLeft: number | null
}

/**
 * Whether a Hyperliquid agent-wallet account should prompt the customer to
 * reconnect, and why. `auth_failed` (dead/evicted agent) takes priority; failing
 * that, an `agent_valid_until` within 30 days is `expiring_soon` — but only for
 * an otherwise-ACTIVE account. Re-signing the agent wallet can't lift a billing
 * freeze, a suspension, or any other non-active state, so those don't get the
 * expiry banner even if the stale agent happens to be expiring soon too. Non
 * agent-wallet accounts (OAuth/API-key) never match. `now` is injectable for
 * tests.
 *
 * Checks `status` directly rather than composing with `toAccountStatus` — that
 * function takes the full `PublicAccount` and returns UI copy tuned for the
 * generic status pill (its `AUTH_FAILED` message assumes an API-key account),
 * which doesn't fit this function's narrower `Pick<>` input or its
 * Hyperliquid-specific copy.
 */
export function hyperliquidReconnectState(
  account: Pick<PublicAccount, 'authMethod' | 'tradeVenue' | 'status' | 'agentValidUntil'>,
  now: number = Date.now()
): HyperliquidReconnectState {
  const none: HyperliquidReconnectState = { needsReconnect: false, reason: null, daysLeft: null }
  if (!isAgentWalletAccount(account)) return none

  if (account.status === AccountStatus.ACCOUNT_STATUS_AUTH_FAILED) {
    return { needsReconnect: true, reason: 'auth_failed', daysLeft: null }
  }

  if (account.status !== AccountStatus.ACCOUNT_STATUS_ACTIVE) return none

  const validUntil = account.agentValidUntil ? Number(account.agentValidUntil) : 0
  if (validUntil > 0 && validUntil - now <= agentExpiryThresholdMs(account.tradeVenue)) {
    return {
      needsReconnect: true,
      reason: 'expiring_soon',
      daysLeft: daysUntil(validUntil, now) ?? 0,
    }
  }

  return none
}
